import { useAuth0 } from '@auth0/auth0-react';
import type { IModularBenefitsList } from '@ben/columbia/api';
import {
  getBenefitDelays,
  getDropdownOptions,
  getModularBenefitById,
} from '@ben/columbia/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export const usePrefetch = (data?: IModularBenefitsList) => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return;

    /**
     * These are the dropdowns that are used in the create/edit benefit form
     * We need to prefetch them so that they're available when the user
     * navigates to the create/edit benefit page
     */
    const dropdownOptions = [
      'employers',
      'providers',
      'benefit-managers',
      'benefit-requirements',
      'benefit-waiver-reasons-groups',
      'tags',
      'component-keys',
    ];

    const prefetchQueries = async () => {
      for (const option of dropdownOptions) {
        await queryClient.prefetchQuery({
          queryKey: [`dropdownOptionsFor-${option}`],
          queryFn: () => getDropdownOptions(option),
        });
      }

      await queryClient.prefetchQuery({
        queryKey: ['benefit-delays'],
        queryFn: getBenefitDelays,
      });
    };

    void prefetchQueries();
  }, [queryClient, isAuthenticated]);

  useEffect(() => {
    const benefitIds = data?.results?.map((benefit) => benefit.id) || [];

    const prefetchBenefitDetails = async () => {
      for (const benefitId of benefitIds) {
        await queryClient.prefetchQuery({
          queryKey: ['columbiaBenefitDetail', benefitId, undefined],
          queryFn: () => getModularBenefitById(benefitId),
        });
      }
    };

    void prefetchBenefitDetails();
  }, [data, queryClient]);
};
